import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useState,
} from "react";
import { Grid, InputAdornment, TextField, Typography } from "@mui/material";
import { NumericFormat } from "react-number-format";
import TitleLabel from "../utils/FormTitle";
import {
  TextFieldForName,
  styleForTextField
} from "../../../utils/TextField/TextField";
import FormCSDropdown from "./FormCSDropdown";
import FormCSDatePicker from "./FormCSDatePicker";
import {
  getDataFormCSFieldMeta
} from "./formCSDataFormFields";

const FormCSFieldErrorContext = createContext({});

export const FormCSFieldErrorProvider = ({ fieldErrors = {}, children }) => (
  <FormCSFieldErrorContext.Provider value={fieldErrors}>
    {children}
  </FormCSFieldErrorContext.Provider>
);

const useFormCSFieldError = (fieldKey) => {
  const fieldErrors = useContext(FormCSFieldErrorContext);
  return fieldErrors?.[fieldKey] || "";
};

export const getFormCSFieldElementId = (fieldKey) =>
  `form-cs-field-${fieldKey}`;

const NUMERIC_DROPDOWN_KEYS = new Set([
  "sctpInd",
  "fsPrep",
  "appStockConvAsset"
]);

const sgdAdornment = (
  <InputAdornment position='end'>
    <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
      SGD
    </Typography>
  </InputAdornment>
);

const sgdAdornmentBold = (
  <InputAdornment position='end'>
    <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 700 }}>
      SGD
    </Typography>
  </InputAdornment>
);

const FormattedNumberTextField = ({
  value,
  onValueChange,
  allowNegative = true,
  decimalScale = 2,
  inputProps,
  ...textFieldProps
}) => {
  const [focused, setFocused] = useState(false);
  const displayValue = focused && Number(value) === 0 ? "" : value ?? "";

  return (
    <NumericFormat
      customInput={TextField}
      value={displayValue}
      valueIsNumericString
      thousandSeparator=','
      decimalScale={decimalScale}
      allowNegative={allowNegative}
      onValueChange={({ value: numericValue }) => onValueChange(numericValue)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      inputProps={{
        ...inputProps,
        inputMode: decimalScale === 0 ? "numeric" : "decimal"
      }}
      {...textFieldProps}
    />
  );
};

export const FormCSNumberField = memo(function FormCSNumberField({
  fieldKey,
  label,
  info,
  value,
  required = false,
  disabled = false,
  onNumberChange,
  xs = 12,
  sm = 6,
  md = 4,
  sgd = false,
  allowNegative = true,
  wholeNumber = false,
  calculated = false
}) {
  const fieldMeta = getDataFormCSFieldMeta(fieldKey);
  const fieldError = useFormCSFieldError(fieldKey);
  const handleChange = useCallback(
    (nextValue) => onNumberChange(fieldKey, nextValue),
    [fieldKey, onNumberChange]
  );

  return (
    <Grid
      item
      id={getFormCSFieldElementId(fieldKey)}
      data-form-cs-field={fieldKey}
      xs={xs}
      sm={sm}
      md={md}
    >
      <TitleLabel
        title={label ?? fieldMeta.label}
        required={required}
        info={info ?? fieldMeta.description}
      />
      <FormattedNumberTextField
        size='small'
        fullWidth
        value={value}
        onValueChange={handleChange}
        allowNegative={allowNegative}
        decimalScale={wholeNumber ? 0 : 2}
        disabled={disabled || calculated}
        error={Boolean(fieldError)}
        helperText={fieldError}
        sx={{
          ...styleForTextField(),
          bgcolor: calculated ? "grey.100" : "white",
          ...(calculated
            ? {
                "& .MuiOutlinedInput-input": {
                  fontWeight: 600
                }
              }
            : {})
        }}
        InputProps={{
          endAdornment: sgd ? sgdAdornment : undefined
        }}
      />
    </Grid>
  );
});

export const FormCSDropdownField = memo(function FormCSDropdownField({
  fieldKey,
  label,
  info,
  value,
  options,
  placeholder,
  required = false,
  disabled = false,
  onDropdownChange,
  xs = 12,
  sm = 6
}) {
  const fieldMeta = getDataFormCSFieldMeta(fieldKey);
  const fieldError = useFormCSFieldError(fieldKey);
  const handleChange = useCallback(
    (e) => onDropdownChange(fieldKey, e.target.value),
    [fieldKey, onDropdownChange]
  );

  return (
    <Grid
      item
      id={getFormCSFieldElementId(fieldKey)}
      data-form-cs-field={fieldKey}
      xs={xs}
      sm={sm}
    >
      <TitleLabel
        title={label ?? fieldMeta.label}
        required={required}
        info={info ?? fieldMeta.description}
      />
      <FormCSDropdown
        value={value}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
        error={Boolean(fieldError)}
        helperText={fieldError}
      />
    </Grid>
  );
});

export const FormCSTextField = memo(function FormCSTextField({
  fieldKey,
  label,
  info,
  value,
  required = false,
  disabled = false,
  onFieldChange,
  xs = 12,
  sm = 6
}) {
  const fieldMeta = getDataFormCSFieldMeta(fieldKey);
  const fieldError = useFormCSFieldError(fieldKey);
  const handleChange = useCallback(
    (val) => onFieldChange(fieldKey, val),
    [fieldKey, onFieldChange]
  );

  return (
    <Grid
      item
      id={getFormCSFieldElementId(fieldKey)}
      data-form-cs-field={fieldKey}
      xs={xs}
      sm={sm}
    >
      <TitleLabel
        title={label ?? fieldMeta.label}
        required={required}
        info={info ?? fieldMeta.description}
      />
      <TextFieldForName
        fullWidth
        value={value ?? ""}
        onChange={handleChange}
        disabled={disabled}
        debounceMs={0}
        error={Boolean(fieldError)}
        helperText={fieldError}
      />
    </Grid>
  );
});

export const FormCSDateField = memo(function FormCSDateField({
  fieldKey,
  label,
  info,
  value,
  required = false,
  disabled = false,
  onFieldChange,
  xs = 12,
  sm = 6
}) {
  const fieldMeta = getDataFormCSFieldMeta(fieldKey);
  const fieldError = useFormCSFieldError(fieldKey);
  const handleChange = useCallback(
    (next) => onFieldChange(fieldKey, next),
    [fieldKey, onFieldChange]
  );

  return (
    <Grid
      item
      id={getFormCSFieldElementId(fieldKey)}
      data-form-cs-field={fieldKey}
      xs={xs}
      sm={sm}
    >
      <TitleLabel
        title={label ?? fieldMeta.label}
        required={required}
        info={info ?? fieldMeta.description}
      />
      <FormCSDatePicker
        value={value ?? ""}
        onChange={handleChange}
        disabled={disabled}
        error={Boolean(fieldError)}
        helperText={fieldError}
      />
    </Grid>
  );
});

export const FormCSTaxAdjustmentField = memo(function FormCSTaxAdjustmentField({
  fieldKey,
  value,
  disabled,
  onNumberChange,
  allowNegative = true,
  wholeNumber = false,
  calculated = false
}) {
  return (
    <FormCSNumberField
      fieldKey={fieldKey}
      value={value}
      required={!calculated}
      disabled={disabled}
      onNumberChange={onNumberChange}
      xs={12}
      sm={12}
      md={12}
      sgd
      allowNegative={allowNegative}
      wholeNumber={wholeNumber}
      calculated={calculated}
    />
  );
});

export const FormCSBfDonationField = memo(function FormCSBfDonationField({
  fieldKey,
  value,
  disabled,
  onNumberChange,
  xs = 12,
  sm = 6,
  md = 6,
  allowNegative = true,
  wholeNumber = false,
  calculated = false
}) {
  return (
    <FormCSNumberField
      fieldKey={fieldKey}
      value={value}
      required={!calculated}
      disabled={disabled}
      onNumberChange={onNumberChange}
      xs={xs}
      sm={sm}
      md={md}
      sgd
      allowNegative={allowNegative}
      wholeNumber={wholeNumber}
      calculated={calculated}
    />
  );
});

export const FormCSPlSupplementField = memo(function FormCSPlSupplementField({
  fieldKey,
  value,
  disabled,
  onNumberChange,
  xs = 12,
  sm = 12,
  md = 12,
  wholeNumber = false,
  allowNegative = false,
}) {
  return (
    <FormCSNumberField
      fieldKey={fieldKey}
      value={value}
      required
      disabled={disabled}
      onNumberChange={onNumberChange}
      xs={xs}
      sm={sm}
      md={md}
      sgd
      allowNegative={allowNegative}
      wholeNumber={wholeNumber}
    />
  );
});

export const FormCSBfCfPairRow = memo(function FormCSBfCfPairRow({
  bfKey,
  cfKey,
  rowLabel,
  bfValue,
  cfValue,
  disabled,
  onNumberChange,
  bfCalculated = false,
  wholeNumber = false
}) {
  return (
    <Grid item xs={12}>
      <Typography
        variant='caption'
        fontWeight={700}
        color='text.secondary'
        sx={{ display: "block", mb: 1 }}
      >
        {rowLabel}
      </Typography>
      <Grid container spacing={2}>
        <FormCSNumberField
          fieldKey={bfKey}
          value={bfValue}
          required
          disabled={disabled}
          onNumberChange={onNumberChange}
          xs={12}
          sm={6}
          md={6}
          sgd
          allowNegative={false}
          wholeNumber={wholeNumber}
          calculated={bfCalculated}
        />
        <FormCSNumberField
          fieldKey={cfKey}
          value={cfValue}
          required
          disabled={disabled}
          onNumberChange={onNumberChange}
          xs={12}
          sm={6}
          md={6}
          sgd
          allowNegative={false}
          wholeNumber={wholeNumber}
        />
      </Grid>
    </Grid>
  );
});

export const FormCSQualificationAmountField = memo(
  function FormCSQualificationAmountField({ value, disabled, onNumberChange }) {
    const fieldKey = "unutilCALDClaimS23S37";
    const fieldMeta = getDataFormCSFieldMeta(fieldKey);
    const fieldError = useFormCSFieldError(fieldKey);
    const handleChange = useCallback(
      (nextValue) => onNumberChange(fieldKey, nextValue),
      [fieldKey, onNumberChange]
    );

    return (
      <Grid
        item
        id={getFormCSFieldElementId(fieldKey)}
        data-form-cs-field={fieldKey}
        xs={12}
        sx={{ flex: 1 }}
      >
        <TitleLabel
          title={fieldMeta.label}
          info={fieldMeta.description}
        />
        <FormattedNumberTextField
          size='small'
          fullWidth
          value={value}
          onValueChange={handleChange}
          allowNegative={false}
          decimalScale={0}
          disabled={disabled}
          error={Boolean(fieldError)}
          helperText={fieldError}
          sx={{
            ...styleForTextField(),
            "& .MuiOutlinedInput-root": {
              bgcolor: "background.paper",
              borderWidth: 2
            },
            "& .MuiOutlinedInput-input": {
              fontWeight: 700,
              fontSize: "1.05rem"
            }
          }}
          InputProps={{ endAdornment: sgdAdornmentBold }}
        />
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: "block", mt: 1 }}
        >
          Enter the claim amount if you are utilising S23/S37 exemption for
          brought-forward balances.
        </Typography>
      </Grid>
    );
  }
);

export { NUMERIC_DROPDOWN_KEYS };
